/* global window console */
import { MOAT } from "./bakingConstants.js";
import { getPinsForMoving, getWeights } from "../../helpers/grid.js";
import { doesOverride, toPerc } from "./bakingHelpers.js";
import { getIconSize } from "../../helpers/iconUtils.js";

export function calculateShapeCoords(left, top, gridd, defaultPadding) {
  // Required calculations and coordinate space transformations
  const wcols = getWeights(gridd.structure.columns);
  const wcSum = wcols.reduce((sum, a, i) => (gridd.structure.columns[i][0] !== "s" ? sum + a : sum), 0);
  const wcsSum = wcols.reduce((sum, a, i) => (gridd.structure.columns[i][0] === "s" ? sum + a : sum), 0);
  const wrows = getWeights(gridd.structure.rows);
  const wrSum = wrows.reduce((sum, a, i) => (gridd.structure.rows[i][0] !== "s" ? sum + a : sum), 0);
  const wrsSum = wrows.reduce((sum, a, i) => (gridd.structure.rows[i][0] === "s" ? sum + a : sum), 0);
  const unit = (window.innerWidth - 2 * MOAT - wcsSum) / wcSum;
  const hUnit = (window.innerHeight - 2 * MOAT - wrsSum) / wrSum;

  // console.log("Baking starting calculations", {
  //   gridd,
  //   left,
  //   top,
  //   wcols,
  //   wcSum,
  //   wcsSum,
  //   wrows,
  //   wrSum,
  //   wrsSum,
  //   unit,
  //   hUnit,
  // });

  const cellsCoords = { borderWeights: { cols: [], rows: [] } };
  try {
    let rt = MOAT + top;
    gridd.structure.rows.forEach((row, ri) => {
      // add the size of the largest border horizontal at ri
      const borderHeight = gridd.borders
        .filter((b) => !b.isVertical && b.index === ri)
        .reduce((r, n) => (n.weight > r ? n.weight : r), 0);
      //console.log("Border width to add to top", { borderHeight, borders: gridd.borders, ri });
      cellsCoords.borderWeights.rows.push(borderHeight);
      rt += borderHeight;

      let cl = MOAT + left;
      const height = row[0] !== "s" ? hUnit * wrows[ri] : parseInt(row.slice(1));
      gridd.structure.columns.forEach((col, ci) => {
        // add the size of the largest border vertical at ci
        const borderWidth = gridd.borders
          .filter((b) => b.isVertical && b.index === ci)
          .reduce((r, n) => (n.weight > r ? n.weight : r), 0);
        //console.log("Border width to add to left", { borderWidth, borders: gridd.borders, ci });
        if (cellsCoords.borderWeights.cols.length < gridd.structure.columns.length) {
          cellsCoords.borderWeights.cols.push(borderWidth);
        }
        cl += borderWidth;

        const width = col[0] !== "s" ? unit * wcols[ci] : parseInt(col.slice(1));

        const merged = gridd.structure.merged.find((m) => m.cell.x === ri && m.cell.y === ci);
        const ch =
          (merged?.dir === "down"
            ? height +
              wrows
                .slice(ri, ri + merged.length) // TODO: doesn't cover spacers properly
                .reduce((acc, next, i) => (acc += gridd.structure.rows[ri + i][0] !== "s" ? hUnit * next : next), 0)
            : height) - borderHeight;

        const cw =
          (merged?.dir === "right"
            ? width +
              wcols
                .slice(ci, ci + merged.length) // TODO: doesn't cover spacers properly
                .reduce((acc, next, i) => (acc += gridd.structure.columns[ci + i][0] !== "s" ? unit * next : next), 0)
            : width) - borderWidth;

        // if (merged) {
        //   console.log("calculating merged cell dimensions", {
        //     width,
        //     height,
        //     merged,
        //     wcols,
        //     wrows,
        //     ch,
        //     cw,
        //     ci,
        //     ri,
        //   });
        // }

        if (col[0] !== "s") {
          const id = gridd.structure.cells[ri][ci].id;
          const cellStyle = gridd.cellStyles[id] ?? {};
          const s = cellStyle.shape ?? {};

          const smh = (s.margin ?? 0) + ch * toPerc(s.marginPerc);
          const smt = doesOverride(s, "marginTop") ? (s.marginTop ?? 0) + ch * toPerc(s.marginTopPerc) : smh;
          const smb = doesOverride(s, "marginBottom") ? (s.marginBottom ?? 0) + ch * toPerc(s.marginBottomPerc) : smh;
          const smw = (s.margin ?? 0) + cw * toPerc(s.marginPerc);
          const sml = doesOverride(s, "marginLeft") ? (s.marginLeft ?? 0) + cw * toPerc(s.marginLeftPerc) : smw;
          const smr = doesOverride(s, "marginRight") ? (s.marginRight ?? 0) + cw * toPerc(s.marginRightPerc) : smw;
          const sh = ch - smt - smb;
          const sw = cw - sml - smr;
          const sl = cl + sml;
          const st = rt + smt;
          const shape = {
            left: sl,
            top: st,
            width: sw,
            height: sh,
          };

          if (cellStyle.shapeGroupId) {
            const x = ri;
            const y = ci;
            const groupStyle = gridd.cellStyles.shapeGroups[cellStyle.shapeGroupId];
            const gap = groupStyle.gap ?? 4;
            const margin = groupStyle.margin ?? 4;
            const reverse = groupStyle.reverse;

            const otherCellIds = Object.keys(gridd.cellStyles).filter(
              (k) => gridd.cellStyles[k].shapeGroupId === cellStyle.shapeGroupId
            );
            const otherCellCoords = gridd.structure.cells
              .map((row, ri) => {
                const cols = row.map((cell, ci) => {
                  if (otherCellIds.includes(cell.id)) {
                    // belongs to my group, return its coords
                    return { y: ci, x: ri, id: cell.id };
                  }
                  return null;
                });
                return cols;
              })
              .reduce((acc, next) => (next ? [...acc, ...next] : acc), [])
              .filter((c) => c !== null);

            const myIndex = otherCellCoords.findIndex((c) => c.x === x && c.y === y);
            const myLevel = reverse ? otherCellCoords.length - myIndex - 1 : myIndex;
            const horizontal = otherCellCoords.every((c) => c.x === x);
            const cpins = getPinsForMoving(gridd.structure.columns, window.innerWidth);
            const rpins = getPinsForMoving(gridd.structure.rows, window.innerHeight);
            const width = cpins[y + 1] - cpins[y];
            const height = rpins[x + 1] - rpins[x];
            const baseCellCoord = reverse ? otherCellCoords[0] : otherCellCoords[otherCellCoords.length - 1];
            let totalHeight, shapeTotalHeight, shapeBaseWidth, sideLength, alpha, gamma;
            let d, topHeight, dt, bottomHeight, db, bg, ratio;

            if (horizontal) {
              totalHeight = otherCellCoords.map((c) => cpins[c.y + 1] - cpins[c.y]).reduce((a, n) => (a += n), 0);
              shapeTotalHeight = totalHeight - 2 * margin;
              shapeBaseWidth = rpins[baseCellCoord.x + 1] - rpins[baseCellCoord.x] - 2 * margin;
              sideLength = Math.sqrt(
                (shapeBaseWidth / 2) * (shapeBaseWidth / 2) +
                  shapeTotalHeight * shapeTotalHeight -
                  2 * shapeBaseWidth * shapeTotalHeight * Math.cos(Math.PI / 2)
              );
              alpha = Math.asin(shapeTotalHeight / sideLength);
              gamma = Math.PI / 2 - alpha;

              if (myLevel === 0) {
                d = ((width - gap) * Math.sin(gamma)) / Math.sin(alpha);
              } else {
                topHeight =
                  otherCellCoords
                    .slice(0, myLevel)
                    .map((c) => cpins[c.y + 1] - cpins[c.y])
                    .reduce((a, n) => (a += n), 0) + gap;
                dt = (topHeight * Math.sin(gamma)) / Math.sin(alpha);
                bg = myLevel === otherCellIds.length - 1 ? margin : gap;
                bottomHeight =
                  otherCellCoords
                    .slice(0, myLevel + 1)
                    .map((c) => cpins[c.y + 1] - cpins[c.y])
                    .reduce((a, n) => (a += n), 0) - bg;
                db = (bottomHeight * Math.sin(gamma)) / Math.sin(alpha);

                const lastCell = otherCellCoords[otherCellCoords.length - 1];
                const th = shapeTotalHeight - (cpins[lastCell.y + 1] - cpins[lastCell.y]);
                const wt = (th * Math.sin(gamma)) / Math.sin(alpha);
                ratio = wt / (shapeBaseWidth / 2);
              }
            } else {
              totalHeight = otherCellCoords.map((c) => rpins[c.x + 1] - rpins[c.x]).reduce((a, n) => (a += n), 0);
              shapeTotalHeight = totalHeight;
              shapeBaseWidth = cpins[baseCellCoord.y + 1] - cpins[baseCellCoord.y] - 2 * margin;
              sideLength = Math.sqrt(
                (shapeBaseWidth / 2) * (shapeBaseWidth / 2) +
                  shapeTotalHeight * shapeTotalHeight -
                  2 * shapeBaseWidth * shapeTotalHeight * Math.cos(Math.PI / 2)
              );
              alpha = Math.asin(shapeTotalHeight / sideLength);
              gamma = Math.PI / 2 - alpha;

              if (myLevel === 0) {
                // render triangle
                d = ((height - gap) * Math.sin(gamma)) / Math.sin(alpha);
              } else {
                topHeight =
                  otherCellCoords
                    .slice(0, myLevel)
                    .map((c) => rpins[c.x + 1] - rpins[c.x])
                    .reduce((a, n) => (a += n), 0) + gap;
                dt = (topHeight * Math.sin(gamma)) / Math.sin(alpha);
                bg = myLevel === otherCellCoords.length - 1 ? margin : gap;
                bottomHeight =
                  otherCellCoords
                    .slice(0, myLevel + 1)
                    .map((c) => rpins[c.x + 1] - rpins[c.x])
                    .reduce((a, n) => (a += n), 0) - bg;
                db = (bottomHeight * Math.sin(gamma)) / Math.sin(alpha);

                const lastCell = otherCellCoords[otherCellCoords.length - 1];
                const th = shapeTotalHeight - (rpins[lastCell.x + 1] - rpins[lastCell.x]);
                const wt = (th * Math.sin(gamma)) / Math.sin(alpha);
                ratio = wt / (shapeBaseWidth / 2);
              }
            }

            shape.grouped = {
              myIndex,
              myLevel,
              horizontal,
              d,
              dt,
              db,
              bg,
              topHeight,
              bottomHeight,
              alpha,
              gamma,
              gap,
              margin,
              reverse,
              totalHeight,
              shapeTotalHeight,
              shapeBaseWidth,
              sideLength,
              ratio,
              otherCellCoords,
              rpins,
              cpins,
              r: dt / db,
            };
          }

          const tph = (cellStyle.padding ?? defaultPadding ?? 0) + sh * toPerc(cellStyle.paddingPerc);
          const tpt = doesOverride(cellStyle, "paddingTop")
            ? (cellStyle.paddingTop ?? 0) + sh * toPerc(cellStyle.paddingTopPerc)
            : tph;
          const tpb = doesOverride(cellStyle, "paddingBottom")
            ? (cellStyle.paddingBottom ?? 0) + sh * toPerc(cellStyle.paddingBottomPerc)
            : tph;
          const tpw = (cellStyle.padding ?? defaultPadding ?? 0) + (sw === 0 ? cw : sw) * toPerc(cellStyle.paddingPerc);
          const tpl = doesOverride(cellStyle, "paddingLeft")
            ? (cellStyle.paddingLeft ?? 0) + sh * toPerc(cellStyle.paddingLeftPerc)
            : tpw;
          const tpr = doesOverride(cellStyle, "paddingRight")
            ? (cellStyle.paddingRight ?? 0) + sh * toPerc(cellStyle.paddingRightPerc)
            : tpw;

          let text = {};
          let icon = {};
          let imt = 0;
          let imb = 0;
          let iml = 0;
          let imr = 0;
          let is = 0;
          if (cellStyle.icon) {
            is = getIconSize(cellStyle.iconSize);
            const i = cellStyle.iconStyle ?? {};
            switch (cellStyle.iconAlign) {
              case "left": {
                imt =
                  sh * (0.5 - toPerc(i.marginTopPerc ?? i.marginPerc) + toPerc(i.marginBottomPerc ?? i.marginPerc)) -
                  is * 0.5 +
                  ((i.marginTop ?? i.margin ?? 0) - (i.marginBottom ?? i.margin ?? 0));
                iml = (i.marginLeft ?? i.margin ?? 0) + is * toPerc(i.marginLeftPerc ?? i.marginPerc);
                imr = (i.marginRight ?? i.margin ?? 0) + is * toPerc(i.marginRightPerc ?? i.marginPerc);

                icon = {
                  left: sl + iml,
                  top: st + imt,
                  width: is,
                  height: is,
                };
                text = {
                  left: sl + iml + is + imr + tpl,
                  top: st + tpt,
                  width: sw - tpl - tpr - iml - is - imr,
                  height: sh - tpt - tpb,
                  margin: {
                    left: tpl,
                    right: tpr,
                    top: tpt,
                    bottom: tpb,
                  },
                };
                break;
              }
              case "right": {
                imt =
                  sh * (0.5 - toPerc(i.marginTopPerc ?? i.marginPerc) + toPerc(i.marginBottomPerc ?? i.marginPerc)) -
                  is * 0.5 +
                  ((i.marginTop ?? i.margin ?? 0) - (i.marginBottom ?? i.margin ?? 0));
                iml = (i.marginLeft ?? i.margin ?? 0) + is * toPerc(i.marginLeftPerc ?? i.marginPerc);
                imr = (i.marginRight ?? i.margin ?? 0) + is * toPerc(i.marginRightPerc ?? i.marginPerc);

                icon = {
                  left: sl + sw - imr - is,
                  top: st + imt,
                  width: is,
                  height: is,
                };
                text = {
                  left: sl + tpl,
                  top: st + tpt,
                  width: sw - tpl - tpr - iml - is - imr,
                  height: sh - tpt - tpb,
                  margin: {
                    left: tpl,
                    right: tpr,
                    top: tpt,
                    bottom: tpb,
                  },
                };
                console.log("Right aligned icon calcs", { icon, text, imt, iml, imr, i, sw, st });
                break;
              }
              default: {
                imt = (i.marginTop ?? i.margin ?? 0) + toPerc(i.marginTopPerc ?? i.marginPerc) * is;
                imb = (i.marginBottom ?? i.margin ?? 0) + toPerc(i.marginBottomPerc ?? i.marginPerc) * is;
                iml =
                  sw * (0.5 - (i.marginRightPerc ?? i.marginPerc ?? 0) + toPerc(i.marginLeftPerc ?? i.marginPerc)) -
                  is * 0.5 +
                  ((i.marginLeft ?? i.margin ?? 0) - (i.marginRight ?? i.margin ?? 0));

                const iconRatio = (cellStyle.iconRatio ?? 50) / 100;
                const iconSectionHeight = sh * iconRatio;
                const it = st + iconSectionHeight - is + imt;
                icon = {
                  left: sl + iml,
                  top: it,
                  width: is,
                  height: is,
                };
                text = {
                  left: sl + tpl,
                  top: st + iconSectionHeight + imb + tpt,
                  width: sw - tpl - tpr,
                  height: sh * (1 - iconRatio) - imb - tpt - tpb,
                  margin: {
                    left: tpl,
                    right: tpr,
                    top: tpt,
                    bottom: tpb,
                  },
                };
                break;
              }
            }
          } else {
            text = {
              left: cl + sml + tpl,
              top: rt + smt + tpt,
              width: sw - tpl - tpr,
              height: sh - tpt - tpb,
              margin: {
                left: tpl,
                right: tpr,
                top: tpt,
                bottom: tpb,
              },
            };
          }

          cellsCoords[id] = {
            id: gridd.structure.cells[ri][ci].id,
            left: cl,
            top: rt,
            width: cw,
            height: ch,
            shape,
            text,
            icon,
            zIndex: merged ? 1 : undefined,
          };

          if (cellsCoords[id].shape.width < 0 || cellsCoords[id].shape.height < 0) {
            console.error("Invalid shape width/height", {
              coords: cellsCoords[id],
              cellStyle,
              ch,
              cw,
              sh,
              sw,
              s,
              smt,
              smb,
              sml,
              smr,
              smh,
              smw,
              sl,
              st,
            });
          }
          if (cellsCoords[id].text.width < 0 || cellsCoords[id].text.height < 0) {
            console.error("Invalid text width/height", {
              coords: cellsCoords[id],
              cellStyle,
              ch,
              cw,
              sh,
              sw,
              s,
              smt,
              smb,
              sml,
              smr,
              smh,
              smw,
              sl,
              st,
              tpt,
              tpb,
              tpl,
              tpr,
              tph,
              tpw,
              is,
              imt,
              imb,
              iml,
              imr,
            });
          }
          if (cellsCoords[id].top <= 0 || cellsCoords[id].shape.top <= 0 || cellsCoords[id].text.top <= 0) {
            console.warn("Resulting shape has invalid top coordinate", {
              coords: cellsCoords[id],
              cellStyle,
              ch,
              cw,
              sh,
              sw,
              s,
              smt,
              smb,
              sml,
              smr,
              smh,
              smw,
              sl,
              st,
              tpt,
              tpb,
              tpl,
              tpr,
              tph,
              tpw,
              is,
              imt,
              imb,
              iml,
              imr,
            });
          }
          if (cellsCoords[id].left <= 0 || cellsCoords[id].shape.left <= 0 || cellsCoords[id].text.left <= 0) {
            console.warn("Resulting shape has invalid left coordinate", {
              coords: cellsCoords[id],
              cellStyle,
              ch,
              cw,
              sh,
              sw,
              s,
              smt,
              smb,
              sml,
              smr,
              smh,
              smw,
              sl,
              st,
              tpt,
              tpb,
              tpl,
              tpr,
              tph,
              tpw,
              is,
              imt,
              imb,
              iml,
              imr,
            });
          }
        }
        cl += width;
      });

      rt += height;
    });

    // console.log("Baking calculations done", {
    //   MOAT,
    //   wcols,
    //   wcSum,
    //   wrows,
    //   wrSum,
    //   unit,
    //   hUnit,
    //   cellsCoords,
    // });
  } catch (error) {
    console.error("Error during baking calculations, aborting baking.", error);
    throw error;
  }
  return cellsCoords;
}
